import React, { useCallback, useEffect, useState } from 'react';
import {
  ButtonAsAnchor, Figure, Heading, Icon, Panel,
} from '@printdeal/ui-library';
import { Popover } from 'react-tiny-popover';
import { FormattedMessage } from 'react-intl';
import { dispatchDataLayerEntry } from '../../helpers/dataLayerHelpers/productDataLayerHelper';

export interface Hotspot {
  id: string;
  title: string;
  content: string;
  percentageX: number;
  percentageY: number;
  featuredImageURL?: string;
  url?: string;
}

export const InteractiveHotspot = ({ hotspot }: { hotspot: Hotspot }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    if (isPopoverOpen) {
      dispatchDataLayerEntry(
        'open hotspot popover',
        'click',
        {
          experiment: 'ninjas_exp1489_add_clickable_products_on_pictures',
          hotspotTitle: hotspot.title,
          isPopoverOpen,
        },
      );
    }
  }, [hotspot.title, isPopoverOpen]);

  const togglePopover = useCallback(() => {
    setIsPopoverOpen(!isPopoverOpen);
    dispatchDataLayerEntry(
      'close hotspot popover',
      'click',
      {
        experiment: 'ninjas_exp1489_add_clickable_products_on_pictures',
        hotspotTitle: hotspot.title,
        isPopoverOpen,
      },
    );
  }, [hotspot.title, isPopoverOpen]);

  return (
    <Popover
      containerClassName="pd-z-50"
      isOpen={isPopoverOpen}
      positions={['right', 'left', 'top', 'bottom']}
      padding={20}
      onClickOutside={togglePopover}
      content={() => (
        <Panel
          className="pd-max-w-[250px] pd-transform pd-rounded-md pd-bg-white pd-p-0 pd-shadow-md"
        >
          <div className="pd-p-4">
            <div className="pd-mb-3 pd-flex pd-items-center pd-justify-between">
              <Heading variant="display" as="p" size="3">{hotspot.title}</Heading>
              <Icon
                icon="times"
                size="sm"
                color="black"
                onClick={togglePopover}
              />
            </div>
            {hotspot.featuredImageURL && (
            <Figure as="img" src={hotspot.featuredImageURL} alt={hotspot.title} width="200" height="200" />
            )}
            <p className="pd-mt-3">{hotspot.content}</p>
          </div>
          <hr />
          {hotspot.url && (
          <div className="pd-p-4">
            <ButtonAsAnchor
              href={hotspot.url}
              onClick={() => {
                dispatchDataLayerEntry(
                  'show product button',
                  'click',
                  {
                    experiment: 'ninjas_exp1489_add_clickable_products_on_pictures',
                    hotspotTitle: hotspot.title,
                    productPage: hotspot.url,
                  },
                );
              }}
              variant="primary"
              className="pd-w-full"
            >
              <FormattedMessage id="product.listing.page.banner.button" defaultMessage="See product" />
            </ButtonAsAnchor>
          </div>
          )}
        </Panel>
      )}
    >
      <button
        aria-label={hotspot.title}
        className="pd-absolute pd-transform pd-cursor-pointer pd-border-none pd-bg-transparent"
        type="button"
        style={{
          left: `${hotspot.percentageX}%`,
          top: `${hotspot.percentageY}%`,
          transform: 'translate(-50%, -50%)',
        }}
        onClick={togglePopover}
      >
        <div className="hotspot" />
      </button>
    </Popover>
  );
};
